import React, { FC } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

export type StatusBadgeVariant =
  | "active"
  | "live"
  | "paused"
  | "inactive"
  | "draft"
  | "scheduled"
  | "archived"
  | "processing"
  | "error";

type StatusBadgeProps = {
  status: string;
  variant?: StatusBadgeVariant;
};

const Badge = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 10px",
  borderRadius: 20,
  fontSize: 11,
  fontWeight: 500,
  '&[data-variant="active"], &[data-variant="live"]': {
    color: "#166534",
    background: "#DCFCE7",
  },
  '&[data-variant="paused"]': {
    color: "#F59E0B",
    background: "#FFFBEB",
  },
  '&[data-variant="inactive"]': {
    color: "#e53e3e",
    background: "#FFF5F5",
  },
  '&[data-variant="archived"]': {
    color: "#6B7280",
    background: "#F3F4F6",
  },
  '&[data-variant="draft"]': {
    color: theme.palette.brand.gray500,
    background: theme.palette.brand.gray50,
  },
  '&[data-variant="scheduled"]': {
    color: theme.palette.brand.blueAccent,
    background: "#EBF4FF",
  },
  '&[data-variant="processing"]': {
    color: "#92400E",
    background: "#FEF3C7",
  },
  '&[data-variant="error"]': {
    color: "#991B1B",
    background: "#FEE2E2",
  },
}));

const Dot = styled("span")({
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: "currentColor",
  flexShrink: 0,
});

const resolveVariant = (status: string, variant?: StatusBadgeVariant): StatusBadgeVariant => {
  if (variant) return variant;
  const normalized = status.toLowerCase();
  if (normalized === "active" || normalized === "live") return "active";
  if (normalized === "inactive" || normalized === "paused") return "inactive";
  if (normalized === "scheduled") return "scheduled";
  if (normalized === "archived") return "archived";
  if (normalized === "processing") return "processing";
  if (normalized === "error") return "error";
  return "draft";
};

const StatusBadge: FC<StatusBadgeProps> = ({ status, variant }) => {
  const resolved = resolveVariant(status, variant);

  return (
    <Badge data-variant={resolved}>
      <Dot />
      {status}
    </Badge>
  );
};

export default StatusBadge;
